import { useState, useCallback, useMemo, memo, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { Warning, Error, CheckCircle, HelpOutline } from "@mui/icons-material";
import {
  CompanyInfo,
  CsvImportRow,
  DuplicateAction,
  DuplicateActionType,
} from "../model";
import { useBuildingTypeAllQuery } from "../../../referentiels/buildingType";
import { useOwnershipTypeAllQuery } from "../../../referentiels/ownershipType";
import { useVirtualizer } from "@tanstack/react-virtual";

export interface ImportPreviewTableProps {
  rows: CsvImportRow[];
  duplicateActions: Map<number, DuplicateActionType>;
  onDuplicateActionChange: (rowNumber: number, action: DuplicateActionType) => void;
  onBulkDuplicateActionChange: (action: DuplicateActionType) => void;
  editedValues: Map<number, Partial<CsvImportRow>>;
  onCellEdit: (rowNumber: number, field: keyof CsvImportRow, value: any) => void;
  excludedRows: Set<number>;
  onToggleRowExcluded: (rowNumber: number) => void;
}

const COLUMN_COUNT = 21;

const getStatusName = (status: any): string => {
  if (!status) return "UNKNOWN";
  if (typeof status === "string") return status;
  if (typeof status === "object" && status.name) return status.name;
  return String(status);
};

const getEnumName = (value: any): string => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && value.name) return value.name;
  return String(value);
};

const getStatusChip = (status: any) => {
  const statusName = getStatusName(status);
  if (statusName === "VALID") {
    return <Chip icon={<CheckCircle />} label="Valide" size="small" color="success" />;
  }
  if (statusName === "INVALID") {
    return <Chip icon={<Error />} label="Invalide" size="small" color="error" />;
  }
  if (statusName === "DUPLICATE") {
    return <Chip icon={<Warning />} label="Doublon" size="small" color="warning" />;
  }
  if (statusName === "WARNING") {
    return <Chip icon={<HelpOutline />} label="Attention" size="small" color="info" />;
  }
  return <Chip label={statusName} size="small" />;
};

const getDuplicateMatchLabel = (matchType: string | undefined): string => {
  switch (matchType) {
    case "EXACT_ID":
      return "ID externe identique";
    case "NAME_LOCATION":
      return "Nom et commune identiques";
    case "COORDINATES":
      return "Coordonnées proches";
    default:
      return "Correspondance détectée";
  }
};

// Plain HTML input styles — avoids MUI TextField overhead (~10 DOM nodes each)
const inputStyle: React.CSSProperties = {
  fontSize: "0.75rem",
  border: "none",
  borderBottom: "1px solid rgba(0,0,0,0.2)",
  outline: "none",
  padding: "4px 0",
  background: "transparent",
  fontFamily: "inherit",
  boxSizing: "border-box" as const,
};

const EditableTextCell = memo(({ value, onChange, width = 120 }: {
  value: any;
  onChange: (value: any) => void;
  width?: number;
}) => {
  const [localValue, setLocalValue] = useState(value ?? "");
  return (
    <input
      type="text"
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={() => onChange(localValue || null)}
      style={{ ...inputStyle, width }}
    />
  );
});

const EditableNumberCell = memo(({ value, onChange, width = 80, decimals = false }: {
  value: any;
  onChange: (value: any) => void;
  width?: number;
  decimals?: boolean;
}) => {
  const [localValue, setLocalValue] = useState(value ?? "");
  return (
    <input
      type="number"
      value={localValue}
      step={decimals ? "0.01" : "1"}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={() => {
        if (localValue === "" || localValue == null) {
          onChange(null);
        } else {
          const parsed = decimals ? parseFloat(localValue) : parseInt(localValue, 10);
          onChange(isNaN(parsed) ? null : parsed);
        }
      }}
      style={{ ...inputStyle, width }}
    />
  );
});

const ReferentialTypeSelect = memo(({ value, onChange, options }: {
  value: string;
  onChange: (value: any) => void;
  options: { id: string; name: string }[];
}) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value || null)}
    style={{ ...inputStyle, minWidth: 100, cursor: "pointer" }}
  >
    <option value="">-</option>
    {options.map((item) => (
      <option key={item.id} value={item.id}>{item.name}</option>
    ))}
  </select>
));

const badgeStyle = (exists: boolean): React.CSSProperties => ({
  fontSize: "0.6rem",
  padding: "0 4px",
  borderRadius: 8,
  backgroundColor: exists ? "#e8f5e9" : "#fff3e0",
  color: exists ? "#2e7d32" : "#e65100",
  lineHeight: "16px",
  whiteSpace: "nowrap",
});

const removeBtnStyle: React.CSSProperties = {
  border: "none",
  background: "none",
  cursor: "pointer",
  padding: 0,
  fontSize: 14,
  lineHeight: 1,
  color: "rgba(0,0,0,0.4)",
};

const CompanyCell = memo(({ companies, onRemove }: { companies: CompanyInfo[]; onRemove?: (index: number) => void }) => {
  if (!companies || companies.length === 0) {
    return <span style={{ fontSize: "0.75rem", color: "rgba(0,0,0,0.4)" }}>-</span>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {companies.map((c, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>{c.name}</span>
          <span style={badgeStyle(!!c.exists)}>{c.exists ? "Existant" : "Nouveau"}</span>
          {onRemove && (
            <button onClick={() => onRemove(i)} style={removeBtnStyle}>×</button>
          )}
        </div>
      ))}
    </div>
  );
});

interface RowContext {
  editedValues: Map<number, Partial<CsvImportRow>>;
  duplicateActions: Map<number, DuplicateActionType>;
  excludedRows: Set<number>;
  onCellEdit: (rowNumber: number, field: keyof CsvImportRow, value: any) => void;
  onDuplicateActionChange: (rowNumber: number, action: DuplicateActionType) => void;
  onToggleRowExcluded: (rowNumber: number) => void;
  buildingTypeOptions: { id: string; name: string }[];
  ownershipTypeOptions: { id: string; name: string }[];
}

const getCellValue = (row: CsvImportRow, field: keyof CsvImportRow, editedRow: Partial<CsvImportRow> | undefined) => {
  if (editedRow && field in editedRow) {
    return editedRow[field];
  }
  return row[field];
};

const getCompanies = (row: CsvImportRow, field: keyof CsvImportRow, editedRow: Partial<CsvImportRow> | undefined): CompanyInfo[] => {
  if (editedRow && field in editedRow) return editedRow[field] as CompanyInfo[];
  return (row[field] as CompanyInfo[]) || [];
};

// Uses a ref for context to avoid re-renders — VirtualRow reads ctx.current on interaction
// duplicateAction and excluded are passed as direct props so memo detects bulk state changes
const VirtualRow = memo(({ row, ctxRef, index, duplicateAction, excluded }: {
  row: CsvImportRow;
  ctxRef: React.RefObject<RowContext>;
  index: number;
  duplicateAction: DuplicateActionType | undefined;
  excluded: boolean;
}) => {
  const ctx = ctxRef.current!;
  const statusName = getStatusName(row.status);
  const isDuplicate = statusName === "DUPLICATE";
  const isInvalid = statusName === "INVALID";
  const editedRow = ctx.editedValues.get(row.rowNumber);

  const handleEdit = useCallback((field: keyof CsvImportRow) => (value: any) => {
    ctxRef.current!.onCellEdit(row.rowNumber, field, value);
  }, [row.rowNumber, ctxRef]);

  const handleRemoveCompany = useCallback((field: keyof CsvImportRow, index: number) => {
    const c = ctxRef.current!;
    const ed = c.editedValues.get(row.rowNumber);
    const current = getCompanies(row, field, ed);
    c.onCellEdit(row.rowNumber, field, current.filter((_, i) => i !== index));
  }, [row, ctxRef]);
  const errors = (row.errors as any[]) || [];
  const rowBg = excluded
    ? "#f5f5f5"
    : isInvalid
    ? (index % 2 === 1 ? "#f9dada" : "#fdeded")
    : isDuplicate
    ? (index % 2 === 1 ? "#ffebcc" : "#fffaf2")
    : (index % 2 === 1 ? "#f5f5f5" : "#fff");

  return (
    <>
      <TableCell>{row.rowNumber}</TableCell>
      <TableCell>{getStatusChip(row.status)}</TableCell>
      <TableCell>
        <EditableTextCell value={getCellValue(row, "name", editedRow)} onChange={handleEdit("name")} width={140} />
      </TableCell>
      <TableCell>
        <EditableTextCell value={getCellValue(row, "commune", editedRow)} onChange={handleEdit("commune")} width={110} />
      </TableCell>
      <TableCell>
        <EditableTextCell value={getCellValue(row, "codeInsee", editedRow)} onChange={handleEdit("codeInsee")} width={70} />
      </TableCell>
      <TableCell>
        <EditableTextCell value={getCellValue(row, "codePostal", editedRow)} onChange={handleEdit("codePostal")} width={70} />
      </TableCell>
      <TableCell>
        <ReferentialTypeSelect value={getEnumName(getCellValue(row, "buildingType", editedRow))} onChange={handleEdit("buildingType")} options={ctx.buildingTypeOptions} />
      </TableCell>
      <TableCell>
        <ReferentialTypeSelect value={getEnumName(getCellValue(row, "ownershipType", editedRow))} onChange={handleEdit("ownershipType")} options={ctx.ownershipTypeOptions} />
      </TableCell>
      <TableCell>
        <EditableNumberCell value={getCellValue(row, "surface", editedRow)} onChange={handleEdit("surface")} decimals />
      </TableCell>
      <TableCell>
        <EditableNumberCell value={getCellValue(row, "volume", editedRow)} onChange={handleEdit("volume")} decimals />
      </TableCell>
      <TableCell>
        <EditableNumberCell value={getCellValue(row, "deliveryYear", editedRow)} onChange={handleEdit("deliveryYear")} width={60} />
      </TableCell>
      <TableCell>
        <EditableTextCell value={getCellValue(row, "externalId", editedRow)} onChange={handleEdit("externalId")} width={90} />
      </TableCell>
      <TableCell>
        <EditableNumberCell value={getCellValue(row, "latitude", editedRow)} onChange={handleEdit("latitude")} width={70} decimals />
      </TableCell>
      <TableCell>
        <EditableNumberCell value={getCellValue(row, "longitude", editedRow)} onChange={handleEdit("longitude")} width={70} decimals />
      </TableCell>
      <TableCell><CompanyCell companies={getCompanies(row, "projectOwner", editedRow)} onRemove={(i) => handleRemoveCompany("projectOwner", i)} /></TableCell>
      <TableCell><CompanyCell companies={getCompanies(row, "architect", editedRow)} onRemove={(i) => handleRemoveCompany("architect", i)} /></TableCell>
      <TableCell><CompanyCell companies={getCompanies(row, "woodConstructor", editedRow)} onRemove={(i) => handleRemoveCompany("woodConstructor", i)} /></TableCell>
      <TableCell><CompanyCell companies={getCompanies(row, "lamelliste", editedRow)} onRemove={(i) => handleRemoveCompany("lamelliste", i)} /></TableCell>
      <TableCell><CompanyCell companies={getCompanies(row, "interiorCarpenter", editedRow)} onRemove={(i) => handleRemoveCompany("interiorCarpenter", i)} /></TableCell>
      <TableCell><CompanyCell companies={getCompanies(row, "supplier", editedRow)} onRemove={(i) => handleRemoveCompany("supplier", i)} /></TableCell>
      <TableCell style={{ position: "sticky", right: 0, backgroundColor: rowBg, borderLeft: "2px solid rgba(0,0,0,0.12)" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
          <ToggleButtonGroup
            value={
              excluded
                ? "SKIP"
                : isDuplicate
                ? (getStatusName(duplicateAction) !== "UNKNOWN" && getStatusName(duplicateAction) !== "SKIP" ? getStatusName(duplicateAction) : "CREATE_NEW")
                : "CREATE_NEW"
            }
            exclusive
            onChange={(_, newValue) => {
              if (newValue === null) return;
              const c = ctxRef.current!;
              if (newValue === "SKIP") {
                if (!excluded) c.onToggleRowExcluded(row.rowNumber);
                if (isDuplicate) c.onDuplicateActionChange(row.rowNumber, DuplicateAction.valueOf("SKIP"));
              } else {
                if (excluded) c.onToggleRowExcluded(row.rowNumber);
                if (isDuplicate) c.onDuplicateActionChange(row.rowNumber, DuplicateAction.valueOf(newValue));
              }
            }}
            size="small"
            sx={{ "& .MuiToggleButton-root": { fontSize: "0.7rem", py: 0.25, px: 1, textTransform: "none" } }}
          >
            <ToggleButton value="CREATE_NEW">Créer</ToggleButton>
            {isDuplicate && <ToggleButton value="UPDATE_EXISTING">Maj</ToggleButton>}
            <ToggleButton value="SKIP">Ignorer</ToggleButton>
          </ToggleButtonGroup>
          {isDuplicate && row.duplicate && (
            <Typography variant="caption" color="warning.dark">
              {getDuplicateMatchLabel(getStatusName(row.duplicate.matchType))}
            </Typography>
          )}
          {isInvalid && errors.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {errors.slice(0, 3).map((err: any, idx: number) => (
                <Tooltip key={idx} title={err.message || ""}>
                  <Typography variant="caption" color="error" sx={{ display: "block" }}>
                    {err.column}: {err.code}
                  </Typography>
                </Tooltip>
              ))}
              {errors.length > 3 && (
                <Typography variant="caption" color="text.secondary">
                  +{errors.length - 3} autres erreurs
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </TableCell>
    </>
  );
}, (prev, next) => prev.row === next.row && prev.index === next.index
  && prev.duplicateAction === next.duplicateAction && prev.excluded === next.excluded);

const ROW_HEIGHT = 52;

export const ImportPreviewTable = ({
  rows,
  duplicateActions,
  onDuplicateActionChange,
  onBulkDuplicateActionChange,
  editedValues,
  onCellEdit,
  excludedRows,
  onToggleRowExcluded,
}: ImportPreviewTableProps) => {
  const hasDuplicates = rows.some((row) => getStatusName(row.status) === "DUPLICATE");
  const allExcluded = rows.length > 0 && rows.every((r) => excludedRows.has(r.rowNumber));

  // Derive the effective bulk action from current row states
  const bulkAction = useMemo(() => {
    if (allExcluded) return "SKIP";
    if (!hasDuplicates) return allExcluded ? "SKIP" : "CREATE_NEW";
    const duplicateRows = rows.filter(r => getStatusName(r.status) === "DUPLICATE");
    const nonExcluded = duplicateRows.filter(r => !excludedRows.has(r.rowNumber));
    if (nonExcluded.length === 0) return "SKIP";
    const actions = new Set(nonExcluded.map(r => {
      const action = getStatusName(duplicateActions.get(r.rowNumber));
      return (action !== "UNKNOWN" && action !== "SKIP") ? action : "CREATE_NEW";
    }));
    if (actions.size === 1) return Array.from(actions)[0];
    return undefined; // mixed state — no button selected
  }, [rows, excludedRows, duplicateActions, allExcluded, hasDuplicates]);

  const buildingTypeQuery = useBuildingTypeAllQuery();
  const ownershipTypeQuery = useOwnershipTypeAllQuery();
  const buildingTypeOptions = useMemo(() =>
    (buildingTypeQuery.data?.items ?? []).map((item) => ({ id: item.id, name: item.name })),
  [buildingTypeQuery.data]);
  const ownershipTypeOptions = useMemo(() =>
    (ownershipTypeQuery.data?.items ?? []).map((item) => ({ id: item.id, name: item.name })),
  [ownershipTypeQuery.data]);

  // Use a ref so VirtualRow reads latest values without needing prop changes
  const ctxRef = useRef<RowContext>({
    editedValues,
    duplicateActions,
    excludedRows,
    onCellEdit,
    onDuplicateActionChange,
    onToggleRowExcluded,
    buildingTypeOptions,
    ownershipTypeOptions,
  });
  ctxRef.current = {
    editedValues,
    duplicateActions,
    excludedRows,
    onCellEdit,
    onDuplicateActionChange,
    onToggleRowExcluded,
    buildingTypeOptions,
    ownershipTypeOptions,
  };

  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 15,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom = virtualItems.length > 0 ? totalSize - virtualItems[virtualItems.length - 1].end : 0;

  return (
    <TableContainer ref={parentRef} component={Paper} sx={{ maxHeight: 500, overflow: "auto" }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, minWidth: 40 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Statut</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Nom</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Commune</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Code INSEE</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Code Postal</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Propriétaire</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Surface</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Volume BDA</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 70 }}>Année</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>ID externe</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Lat</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Lon</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 130 }}>MONom</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 130 }}>MOE</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 130 }}>Charpentier</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 130 }}>Lamelliste</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 130 }}>Menuisier</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 130 }}>Scieur</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 200, position: "sticky", right: 0, backgroundColor: "#fff", borderLeft: "2px solid", borderLeftColor: "divider", zIndex: 3 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <ToggleButtonGroup
                  value={bulkAction ?? ""}
                  exclusive
                  onChange={(_, newValue) => {
                    if (newValue === null) return;
                    if (newValue === "SKIP") {
                      rows.forEach((r) => {
                        if (!excludedRows.has(r.rowNumber)) onToggleRowExcluded(r.rowNumber);
                      });
                    } else {
                      rows.forEach((r) => {
                        if (excludedRows.has(r.rowNumber)) onToggleRowExcluded(r.rowNumber);
                      });
                      if (hasDuplicates) {
                        onBulkDuplicateActionChange(DuplicateAction.valueOf(newValue));
                      }
                    }
                  }}
                  size="small"
                  sx={{ "& .MuiToggleButton-root": { fontSize: "0.7rem", py: 0.25, px: 1, textTransform: "none" } }}
                >
                  <ToggleButton value="CREATE_NEW">Tout créer</ToggleButton>
                  {hasDuplicates && <ToggleButton value="UPDATE_EXISTING">Tout maj</ToggleButton>}
                  <ToggleButton value="SKIP">Tout ignorer</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paddingTop > 0 && (
            <tr><td colSpan={COLUMN_COUNT} style={{ height: paddingTop, padding: 0, border: "none" }} /></tr>
          )}
          {virtualItems.map((virtualRow) => {
            const row = rows[virtualRow.index];
            const statusName = getStatusName(row.status);
            const isInvalid = statusName === "INVALID";
            const isDuplicate = statusName === "DUPLICATE";
            const excluded = excludedRows.has(row.rowNumber);

            return (
              <tr
                key={row.rowNumber}
                style={{
                  backgroundColor: isInvalid
                    ? (virtualRow.index % 2 === 1 ? "#f9dada" : "#fdeded")
                    : isDuplicate
                    ? (virtualRow.index % 2 === 1 ? "#ffebcc" : "#fffaf2")
                    : (virtualRow.index % 2 === 1 ? "#f5f5f5" : undefined),
                  opacity: excluded ? 0.5 : 1,
                }}
              >
                <VirtualRow row={row} ctxRef={ctxRef} index={virtualRow.index}
                  duplicateAction={duplicateActions.get(row.rowNumber)} excluded={excluded} />
              </tr>
            );
          })}
          {paddingBottom > 0 && (
            <tr><td colSpan={COLUMN_COUNT} style={{ height: paddingBottom, padding: 0, border: "none" }} /></tr>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
